import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  ChartBarIcon as BarChart3,
  BuildingOffice2Icon as Building2,
  ClipboardDocumentCheckIcon as ClipboardCheck,
  ArrowTrendingDownIcon as TrendingDown,
  ArrowTrendingUpIcon as TrendingUp,
} from '@heroicons/react/24/outline';
import type { AssessmentKPIsDto, DashboardOverviewDto } from '../../types/api';

interface Props {
  data: DashboardOverviewDto;
  isLoading?: boolean;
  kpiData?: AssessmentKPIsDto | null;
  kpiLoading?: boolean;
  kpiType?: 'HYGIENE' | 'COMPLIANCE';
  onKpiTypeChange?: (type: 'HYGIENE' | 'COMPLIANCE') => void;
}

// ─── Delta badge — inline pill next to the big number ────────────────────────
function DeltaBadge({ value, unit }: { value: number; unit: 'pct' | 'pp' }) {
  const positive = value > 0;
  const zero = value === 0;
  const formatted = `${positive ? '+' : value < 0 ? '' : ''}${Math.abs(value).toFixed(1)}${unit === 'pp' ? 'pp' : '%'}`;
  const bg = positive ? 'rgba(34,197,94,0.12)' : zero ? 'rgba(107,114,128,0.1)' : 'rgba(239,68,68,0.12)';
  const color = positive ? '#16a34a' : zero ? '#6b7280' : '#dc2626';
  const Icon = positive ? ArrowUpRightIcon : ArrowDownRightIcon;
  return (
    <span
      className='inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-semibold'
      style={{ backgroundColor: bg, color }}
    >
      {!zero && <Icon className='w-2.5 h-2.5' />}
      {formatted}
    </span>
  );
}

// ─── Trend indicator — bottom-right of card ───────────────────────────────────
function TrendIndicator({ value, unit }: { value: number; unit: 'pct' | 'pp' }) {
  const positive = value > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? '#16a34a' : '#dc2626';
  return (
    <div className='flex items-center gap-0.5' style={{ color }}>
      <Icon className='w-3.5 h-3.5' />
      <span className='text-[11px] font-semibold'>
        {Math.abs(value).toFixed(1)}{unit === 'pp' ? 'pp' : '%'}
      </span>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className='bg-white rounded-lg p-5 shadow-sm animate-pulse' style={{ border: '1px solid var(--nhip-border)' }}>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <div className='w-9 h-9 rounded-lg' style={{ backgroundColor: 'var(--nhip-border)' }} />
          <div className='h-3 w-24 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
        </div>
        <div className='h-3 w-16 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
      </div>
      <div className='flex items-baseline gap-2 mb-2'>
        <div className='h-8 w-24 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
        <div className='h-5 w-12 rounded-full' style={{ backgroundColor: 'var(--nhip-border)' }} />
      </div>
      <div className='h-3 w-40 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
    </div>
  );
}

export default function OverviewCards({ data, isLoading, kpiData, kpiLoading, kpiType, onKpiTypeChange }: Props) {
  const allTime = data?.period === 'all-time';

  if (isLoading || !data) {
    return (
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
        {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  const scoreStanding =
    data.averageScore >= 80 ? 'Good standing' :
    data.averageScore >= 60 ? 'Fair standing' : 'Needs attention';

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>

      {/* ── Card 1: Total Facilities ───────────────────────────────────────── */}
      <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border'
              style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
              <Building2 className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
            </div>
            <span className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>Facilities</span>
          </div>
          <span className='text-xs font-medium' style={{ color: 'var(--nhip-ink-muted)' }}>Total</span>
        </div>

        {/* Value + badge */}
        <div className='flex items-baseline gap-2 flex-wrap'>
          <span className='text-3xl font-bold tracking-tight' style={{ color: 'var(--nhip-ink)' }}>
            {data.totalFacilities.toLocaleString()}
          </span>
          {!allTime && data.totalFacilitiesDelta != null && data.totalFacilitiesDelta !== 0 && (
            <DeltaBadge value={data.totalFacilitiesDelta} unit='pct' />
          )}
        </div>

        {/* Description */}
        <p className='text-xs leading-snug' style={{ color: 'var(--nhip-ink-muted)' }}>
          {data.uniqueFacilitiesAssessed.toLocaleString()} assessed ·{' '}
          {data.coverageRate.toFixed(1)}% coverage rate
        </p>

        {/* Footer */}
        <div className='flex items-center justify-end mt-auto pt-1'>
          {!allTime && data.totalFacilitiesDelta != null && data.totalFacilitiesDelta !== 0 ? (
            <TrendIndicator value={data.totalFacilitiesDelta} unit='pct' />
          ) : (
            <span className='text-[11px]' style={{ color: 'var(--nhip-ink-muted)' }}>All facilities</span>
          )}
        </div>
      </div>

      {/* ── Card 2: Assessments ────────────────────────────────────────────── */}
      <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border'
              style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
              <ClipboardCheck className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
            </div>
            <span className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>Assessments</span>
          </div>
          <span className='text-xs font-medium' style={{ color: 'var(--nhip-ink-muted)' }}>
            {allTime ? 'All time' : 'This period'}
          </span>
        </div>

        {/* Value + badge */}
        <div className='flex items-baseline gap-2 flex-wrap'>
          <span className='text-3xl font-bold tracking-tight' style={{ color: 'var(--nhip-ink)' }}>
            {data.totalAssessments.toLocaleString()}
          </span>
          {!allTime && data.firstAssessmentsDelta != null && data.firstAssessmentsDelta !== 0 && (
            <DeltaBadge value={data.firstAssessmentsDelta} unit='pct' />
          )}
        </div>

        {/* Description */}
        <p className='text-xs leading-snug' style={{ color: 'var(--nhip-ink-muted)' }}>
          {data.firstAssessments.toLocaleString()} first assessments ·{' '}
          {data.revisitsCarried.toLocaleString()} revisits
        </p>

        {/* Footer */}
        <div className='flex items-center justify-end mt-auto pt-1'>
          {!allTime && data.firstAssessmentsDelta != null && data.firstAssessmentsDelta !== 0 ? (
            <TrendIndicator value={data.firstAssessmentsDelta} unit='pct' />
          ) : (
            <span className='text-[11px]' style={{ color: 'var(--nhip-ink-muted)' }}>
              {data.revisitsShareOfTotal.toFixed(1)}% revisit share
            </span>
          )}
        </div>
      </div>

      {/* ── Card 3: Avg Hygiene Score ──────────────────────────────────────── */}
      <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border'
              style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
              <BarChart3 className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
            </div>
            <span className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>Avg Score</span>
          </div>
          <span className='text-xs font-medium' style={{ color: 'var(--nhip-ink-muted)' }}>Hygiene</span>
        </div>

        {/* Value + badge */}
        <div className='flex items-baseline gap-2 flex-wrap'>
          <span className='text-3xl font-bold tracking-tight' style={{ color: 'var(--nhip-ink)' }}>
            {data.averageScore.toFixed(1)}%
          </span>
          {!allTime && data.averageScoreDelta != null && data.averageScoreDelta !== 0 && (
            <DeltaBadge value={data.averageScoreDelta} unit='pp' />
          )}
        </div>

        {/* Description */}
        <p className='text-xs leading-snug' style={{ color: 'var(--nhip-ink-muted)' }}>
          {scoreStanding} across all assessed facilities
        </p>

        {/* Footer */}
        <div className='flex items-center justify-end mt-auto pt-1'>
          {!allTime && data.averageScoreDelta != null && data.averageScoreDelta !== 0 ? (
            <TrendIndicator value={data.averageScoreDelta} unit='pp' />
          ) : (
            <span className='text-[11px]' style={{ color: 'var(--nhip-ink-muted)' }}>{scoreStanding}</span>
          )}
        </div>
      </div>

      {/* ── Card 4: Score Journey ──────────────────────────────────────────── */}
      <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border'
              style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
              <TrendingUp className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
            </div>
            <span className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>Score Journey</span>
          </div>
          {/* Hygiene / Compliance toggle */}
          {onKpiTypeChange && (
            <div className='flex items-center gap-0.5 rounded-lg p-0.5 flex-shrink-0'
              style={{ backgroundColor: 'var(--nhip-border)' }}>
              <button
                onClick={() => onKpiTypeChange('HYGIENE')}
                className='px-1.5 py-0.5 text-[10px] font-medium rounded-md transition-all'
                style={{
                  backgroundColor: kpiType === 'HYGIENE' ? 'white' : 'transparent',
                  color: kpiType === 'HYGIENE' ? '#2563eb' : 'var(--nhip-ink-muted)',
                  boxShadow: kpiType === 'HYGIENE' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                Hygiene
              </button>
              <button
                onClick={() => onKpiTypeChange('COMPLIANCE')}
                className='px-1.5 py-0.5 text-[10px] font-medium rounded-md transition-all'
                style={{
                  backgroundColor: kpiType === 'COMPLIANCE' ? 'white' : 'transparent',
                  color: kpiType === 'COMPLIANCE' ? '#2563eb' : 'var(--nhip-ink-muted)',
                  boxShadow: kpiType === 'COMPLIANCE' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                Compliance
              </button>
            </div>
          )}
        </div>

        {kpiLoading ? (
          <div className='flex-1 flex items-center justify-center py-4'>
            <div className='h-3 w-3 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500' />
          </div>
        ) : kpiData ? (
          <>
            {/* Value */}
            <span className='text-3xl font-bold tracking-tight' style={{ color: 'var(--nhip-ink)' }}>
              {kpiData.avgScoreImprovement != null
                ? `${kpiData.avgScoreImprovement >= 0 ? '+' : ''}${kpiData.avgScoreImprovement.toFixed(1)}pp`
                : '—'}
            </span>

            {/* Description */}
            <p className='text-xs leading-snug' style={{ color: 'var(--nhip-ink-muted)' }}>
              {kpiData.revisitAssessments.toLocaleString()} revisit assessments ·{' '}
              {kpiData.revisitedFacilityCount.toLocaleString()} facilities
            </p>
            <p className='text-xs leading-snug flex items-center gap-1.5 flex-wrap'>
              <span style={{ color: '#16a34a' }}>↑{kpiData.improvedCount.toLocaleString()} improved</span>
              <span style={{ color: 'var(--nhip-ink-faint)' }}>·</span>
              <span style={{ color: 'var(--nhip-ink-muted)' }}>={kpiData.unchangedCount.toLocaleString()} same</span>
              <span style={{ color: 'var(--nhip-ink-faint)' }}>·</span>
              <span style={{ color: '#dc2626' }}>↓{kpiData.declinedCount.toLocaleString()} declined</span>
            </p>

            {/* Journey bar + footer */}
            {kpiData.avgFirstAssessmentScore != null && kpiData.avgLatestAssessmentScore != null && (() => {
              const first = kpiData.avgFirstAssessmentScore;
              const latest = kpiData.avgLatestAssessmentScore;
              const improved = latest > first;
              const declined = latest < first;
              const segColor = improved ? '#16a34a' : declined ? '#dc2626' : 'var(--nhip-ink-muted)';
              const low = Math.min(first, latest);
              const high = Math.max(first, latest);
              return (
                <div className='mt-auto pt-2 flex flex-col gap-1.5'>
                  <div className='flex items-center gap-2'>
                    <span className='text-[10px] w-9 text-right flex-shrink-0' style={{ color: 'var(--nhip-ink-muted)' }}>
                      {first.toFixed(1)}%
                    </span>
                    <div className='relative flex-1 h-1.5 rounded-full overflow-hidden'
                      style={{ backgroundColor: 'var(--nhip-border)' }}>
                      <div
                        className='absolute left-0 top-0 h-full'
                        style={{ width: `${low}%`, backgroundColor: '#d1d5db' }}
                      />
                      <div
                        className='absolute top-0 h-full'
                        style={{ left: `${low}%`, width: `${high - low}%`, backgroundColor: segColor }}
                      />
                    </div>
                    <span className='text-[10px] font-bold w-9 flex-shrink-0' style={{ color: segColor }}>
                      {latest.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          <span className='text-3xl font-bold tracking-tight' style={{ color: 'var(--nhip-ink)' }}>—</span>
        )}
      </div>

    </div>
  );
}
